import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../hooks/useAuth";
import { useOrg } from "../../contexts/OrgContext";
import { ThemeToggle } from "../common";
import { LanguageSwitcher } from "../common/LanguageSwitcher";
import {
  BuildingIcon,
  CheckIcon,
  ChevronDownIcon,
  CloseIcon,
  LogoIcon,
  LogoutIcon,
  MenuIcon,
  SettingsIcon,
  UserIcon,
} from "../icons";
import styles from "./Header.module.css";

export function Header() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { activeOrg, switchOrg } = useOrg();
  const navigate = useNavigate();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isOrgSectionExpanded, setIsOrgSectionExpanded] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const userMenuButtonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; right: number } | null>(null);

  const updateDropdownPosition = useCallback(() => {
    if (userMenuButtonRef.current) {
      const rect = userMenuButtonRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right,
      });
    }
  }, []);

  useEffect(() => {
    if (isUserMenuOpen) {
      updateDropdownPosition();
      window.addEventListener("resize", updateDropdownPosition);
      window.addEventListener("scroll", updateDropdownPosition, true);
      return () => {
        window.removeEventListener("resize", updateDropdownPosition);
        window.removeEventListener("scroll", updateDropdownPosition, true);
      };
    }
  }, [isUserMenuOpen, updateDropdownPosition]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        setIsUserMenuOpen(false);
        setIsOrgSectionExpanded(false);
      }
    }

    if (isUserMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isUserMenuOpen]);

  const handleOrgSwitch = (orgId: string) => {
    switchOrg(orgId);
    setIsOrgSectionExpanded(false);
  };

  const getRoleBadgeClass = (role: string) => {
    switch (role) {
      case "owner":
        return styles.roleBadgeOwner;
      case "admin":
        return styles.roleBadgeAdmin;
      default:
        return styles.roleBadgeMember;
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <>
      <header className={styles.appHeader}>
        <div className={styles.headerContainer}>
          <div className={styles.headerLeft}>
            <Link to="/dashboard" className={styles.logo}>
              <LogoIcon />
              <span className={styles.logoText}>{t("common.appName")}</span>
            </Link>

            <nav className={styles.desktopNav}>
              <Link to="/dashboard" className={styles.navLink}>
                {t("navigation.dashboard")}
              </Link>
              <Link to="/voice-profile" className={styles.navLink}>
                {t("navigation.voiceProfile")}
              </Link>
            </nav>
          </div>

          <div className={styles.headerRight}>
            <div className={styles.desktopControls}>
              <ThemeToggle />
              <LanguageSwitcher variant="header" />

              <div className={styles.userMenu} ref={userMenuRef}>
                <button
                  ref={userMenuButtonRef}
                  className={styles.userMenuButton}
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  aria-expanded={isUserMenuOpen}
                  aria-haspopup="true"
                >
                  <div className={styles.userAvatar}>
                    <UserIcon size={18} />
                  </div>
                  <ChevronDownIcon
                    className={`${styles.chevron} ${isUserMenuOpen ? styles.chevronRotate : ""}`}
                  />
                </button>

                {isUserMenuOpen &&
                  dropdownPos &&
                  createPortal(
                    <div
                      ref={dropdownRef}
                      className={styles.userMenuDropdown}
                      style={{ top: dropdownPos.top, right: dropdownPos.right }}
                    >
                      <div className={styles.userInfo}>
                        <div className={styles.userInfoEmail}>{user.email}</div>
                        {user.emailVerified && (
                          <div className={styles.verifiedBadge}>
                            <CheckIcon />
                            {t("auth.verified")}
                          </div>
                        )}
                      </div>

                      {user.organizations && user.organizations.length > 0 && (
                        <div className={styles.orgSection}>
                          <button
                            className={styles.orgSectionHeader}
                            onClick={() => setIsOrgSectionExpanded(!isOrgSectionExpanded)}
                            aria-expanded={isOrgSectionExpanded}
                          >
                            <BuildingIcon />
                            <span className={styles.orgSectionName}>
                              {activeOrg?.name || t("navigation.organizations")}
                            </span>
                            <ChevronDownIcon
                              className={`${styles.orgChevron} ${isOrgSectionExpanded ? styles.orgChevronRotate : ""}`}
                            />
                          </button>

                          {isOrgSectionExpanded && (
                            <div className={styles.orgSubmenu}>
                              <div className={styles.orgList}>
                                {user.organizations.map((org) => (
                                  <button
                                    key={org.id}
                                    className={`${styles.orgItem} ${activeOrg?.id === org.id ? styles.orgItemActive : ""}`}
                                    onClick={() => handleOrgSwitch(org.id)}
                                  >
                                    <div className={styles.orgItemContent}>
                                      <span className={styles.orgItemName}>{org.name}</span>
                                      <span
                                        className={`${styles.roleBadge} ${getRoleBadgeClass(org.role)}`}
                                      >
                                        {org.role}
                                      </span>
                                    </div>
                                    {activeOrg?.id === org.id && (
                                      <CheckIcon className={styles.orgCheckIcon} />
                                    )}
                                  </button>
                                ))}
                              </div>
                              <Link
                                to="/organizations"
                                className={styles.manageOrgsLink}
                                onClick={() => {
                                  setIsOrgSectionExpanded(false);
                                  setIsUserMenuOpen(false);
                                }}
                              >
                                {t("navigation.manageOrganizations", "Manage Organizations")}
                              </Link>
                            </div>
                          )}
                        </div>
                      )}

                      <div className={styles.menuDivider}></div>
                      <Link
                        to="/settings"
                        className={styles.menuItem}
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <SettingsIcon />
                        {t("navigation.settings")}
                      </Link>
                      <div className={styles.menuDivider}></div>
                      <button
                        className={`${styles.menuItem} ${styles.menuItemLogout}`}
                        onClick={handleLogout}
                      >
                        <LogoutIcon />
                        {t("auth.logout")}
                      </button>
                    </div>,
                    document.body,
                  )}
              </div>
            </div>

            <Link
              to="/dashboard"
              className={styles.mobileWriteButton}
              aria-label={t("navigation.newProject", "New project")}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
              >
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            </Link>
            <button
              className={styles.mobileMenuButton}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
            </button>
          </div>
        </div>

        {isMobileMenuOpen && (
          <div className={styles.mobileMenu}>
            <nav className={styles.mobileNav}>
              <Link
                to="/dashboard"
                className={styles.mobileNavLink}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t("navigation.dashboard")}
              </Link>
              <Link
                to="/voice-profile"
                className={styles.mobileNavLink}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t("navigation.voiceProfile")}
              </Link>
              <Link
                to="/organizations"
                className={styles.mobileNavLink}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t("navigation.organizations")}
              </Link>
              <Link
                to="/settings"
                className={styles.mobileNavLink}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t("navigation.settings")}
              </Link>
            </nav>
            <div className={styles.mobileMenuFooter}>
              <div className={styles.mobileControls}>
                <ThemeToggle />
                <LanguageSwitcher variant="header" />
              </div>
              <div className={styles.mobileUserInfo}>
                <div className={styles.userAvatar}>
                  <UserIcon size={18} />
                </div>
                <div className={styles.userDetails}>
                  <div className={styles.userEmailMobile}>{user.email}</div>
                  {user.emailVerified && (
                    <div className={styles.verifiedBadgeMobile}>{t("auth.verified")}</div>
                  )}
                </div>
              </div>
              <button className={styles.mobileLogout} onClick={handleLogout}>
                {t("auth.logout")}
              </button>
            </div>
          </div>
        )}
      </header>
      <div className={styles.headerSpacer} />
    </>
  );
}
